import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { LogIn, LogOut, Clock, ClipboardEdit, Check, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function hoursBetween(a, b) {
  if (!a || !b) return null;
  const mins = (new Date(b) - new Date(a)) / 60000;
  return (mins / 60).toFixed(2);
}

export default function Attendance() {
  const { user } = useAuth();
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [team, setTeam] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [note, setNote] = useState("");
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().slice(0, 10));
  const [lateAfter, setLateAfter] = useState("10:00");
  const [days, setDays] = useState(30);
  const [corrections, setCorrections] = useState([]);
  const [corrOpen, setCorrOpen] = useState(false);
  const [corrForm, setCorrForm] = useState({ date: new Date().toISOString().slice(0, 10), requested_check_in: "", requested_check_out: "", reason: "" });

  const isAdmin = user?.role === "admin";

  const loadMe = async () => {
    const r = await api.get("/attendance/me");
    setToday(r.data.today);
    setHistory(r.data.history);
  };
  const loadTeam = async (d) => {
    try {
      const r = await api.get("/attendance/team", { params: d ? { date: d } : {} });
      setTeam(r.data);
    } catch (_) {}
  };
  const loadAnalytics = async () => {
    try {
      const r = await api.get("/attendance/analytics", {
        params: { days, late_after: lateAfter },
      });
      setAnalytics(r.data);
    } catch (_) {}
  };
  const loadCorrections = async () => {
    try {
      const r = await api.get("/attendance/correction-requests");
      setCorrections(r.data);
    } catch (_) {}
  };

  useEffect(() => {
    loadMe();
    loadAnalytics();
    loadCorrections();
    if (isAdmin) loadTeam(dateFilter);
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) loadTeam(dateFilter);
  }, [dateFilter, isAdmin]);

  useEffect(() => {
    loadAnalytics();
  }, [days, lateAfter]);

  const submitCorrection = async () => {
    if (!corrForm.reason.trim()) { toast.error("Reason required"); return; }
    if (!corrForm.requested_check_in && !corrForm.requested_check_out) { toast.error("Provide at least one time"); return; }
    try {
      const payload = {
        date: corrForm.date,
        reason: corrForm.reason,
        requested_check_in: corrForm.requested_check_in ? new Date(`${corrForm.date}T${corrForm.requested_check_in}`).toISOString() : null,
        requested_check_out: corrForm.requested_check_out ? new Date(`${corrForm.date}T${corrForm.requested_check_out}`).toISOString() : null,
      };
      await api.post("/attendance/correction-request", payload);
      toast.success("Correction request sent to admin");
      setCorrOpen(false);
      setCorrForm({ date: new Date().toISOString().slice(0, 10), requested_check_in: "", requested_check_out: "", reason: "" });
      loadCorrections();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const decideCorrection = async (id, decision) => {
    try {
      await api.post(`/attendance/correction-requests/${id}/decide`, { decision });
      toast.success(decision === "approve" ? "Approved & applied" : "Rejected");
      loadCorrections();
      loadMe();
      loadAnalytics();
      if (isAdmin) loadTeam(dateFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const checkIn = async () => {
    try {
      await api.post("/attendance/checkin", { note });
      setNote("");
      toast.success("Checked in");
      loadMe();
      loadAnalytics();
      if (isAdmin) loadTeam(dateFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const checkOut = async () => {
    try {
      await api.post("/attendance/checkout", { note });
      setNote("");
      toast.success("Checked out");
      loadMe();
      loadAnalytics();
      if (isAdmin) loadTeam(dateFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const checkedIn = today?.check_in && !today?.check_out;
  const done = today?.check_in && today?.check_out;

  return (
    <div data-testid="attendance-page">
      <PageHeader
        eyebrow="Team"
        title="Attendance"
        description="Mark your arrival & departure. Admin verifies the team's timesheet."
      />

      <div className="px-6 lg:px-10 py-8 space-y-10">
        {/* My today card */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-neutral-900 border border-neutral-900">
          <div className="bg-neutral-950 p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">
              Check-In
            </div>
            <div className="font-heading text-3xl">{fmt(today?.check_in)}</div>
          </div>
          <div className="bg-neutral-950 p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">
              Check-Out
            </div>
            <div className="font-heading text-3xl">{fmt(today?.check_out)}</div>
          </div>
          <div className="bg-neutral-950 p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">
              Hours Today
            </div>
            <div className="font-heading text-3xl">
              {hoursBetween(today?.check_in, today?.check_out) || "—"}
            </div>
          </div>
        </div>

        <div className="border border-neutral-900 p-6 flex flex-col md:flex-row gap-3 items-stretch md:items-end">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
              Note (optional)
            </div>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="WFH / On-set / Late due to traffic..."
              className="rounded-none bg-neutral-900 border-neutral-800"
              data-testid="attendance-note"
            />
          </div>
          <Button
            onClick={checkIn}
            disabled={!!today?.check_in}
            data-testid="check-in-btn"
            className="rounded-none bg-[#00C97A] hover:bg-[#00B26B] text-black h-11"
          >
            <LogIn className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Check In
          </Button>
          <Button
            onClick={checkOut}
            disabled={!checkedIn}
            data-testid="check-out-btn"
            className="rounded-none bg-neutral-900 border border-neutral-800 hover:bg-neutral-800 h-11"
          >
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Check Out
          </Button>
        </div>

        {done && (
          <div className="text-xs text-neutral-500 -mt-6">
            <Clock className="w-3 h-3 inline mr-1" /> You've completed today's
            attendance.
          </div>
        )}

        {/* Request correction */}
        <div className="flex justify-end -mt-4">
          <Dialog open={corrOpen} onOpenChange={setCorrOpen}>
            <DialogTrigger asChild>
              <Button variant="ghost" data-testid="request-correction-btn" className="rounded-none text-xs uppercase tracking-[0.15em] hover:bg-neutral-900 h-8">
                <ClipboardEdit className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> Request Correction
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-neutral-950 border border-neutral-800 rounded-none max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Request Attendance Correction</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Date</Label>
                  <Input type="date" value={corrForm.date} onChange={(e) => setCorrForm({ ...corrForm, date: e.target.value })} className="mt-1 rounded-none bg-neutral-900 border-neutral-800" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Check-In</Label>
                    <Input type="time" value={corrForm.requested_check_in} onChange={(e) => setCorrForm({ ...corrForm, requested_check_in: e.target.value })} className="mt-1 rounded-none bg-neutral-900 border-neutral-800" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Check-Out</Label>
                    <Input type="time" value={corrForm.requested_check_out} onChange={(e) => setCorrForm({ ...corrForm, requested_check_out: e.target.value })} className="mt-1 rounded-none bg-neutral-900 border-neutral-800" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Reason</Label>
                  <Textarea value={corrForm.reason} onChange={(e) => setCorrForm({ ...corrForm, reason: e.target.value })} placeholder="Forgot to check out / On-site shoot..." className="mt-1 rounded-none bg-neutral-900 border-neutral-800" />
                </div>
              </div>
              <DialogFooter className="mt-4">
                <Button onClick={submitCorrection} data-testid="submit-correction-btn" className="rounded-none bg-[#00C97A] hover:bg-[#00B26B] text-black">Send to Admin</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Correction requests */}
        {corrections.length > 0 && (
          <div className="border border-neutral-900">
            <div className="px-5 py-4 border-b border-neutral-900 text-[10px] uppercase tracking-[0.3em] text-neutral-500">
              {isAdmin ? "Correction Requests · Inbox" : "Your Correction Requests"}
            </div>
            <div className="divide-y divide-neutral-900">
              {corrections.map((c) => {
                const pending = c.status === "pending";
                return (
                  <div key={c.correction_id} className="px-5 py-3 grid grid-cols-1 md:grid-cols-[1fr,auto] gap-3 items-start" data-testid={`correction-${c.correction_id}`}>
                    <div className="text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{c.user_name}</span>
                        <span className="text-[10px] font-mono text-neutral-500">{c.date}</span>
                        <span className={`text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 ${
                          pending ? "bg-amber-500/10 text-amber-300 border border-amber-500/30" :
                          c.status === "approved" ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" :
                          "bg-red-500/10 text-red-300 border border-red-500/30"
                        }`}>{c.status}</span>
                      </div>
                      <div className="text-xs text-neutral-400 mt-1">
                        {c.requested_check_in && <>In: {new Date(c.requested_check_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} </>}
                        {c.requested_check_out && <>· Out: {new Date(c.requested_check_out).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</>}
                      </div>
                      <div className="text-xs text-neutral-500 mt-1 italic">"{c.reason}"</div>
                    </div>
                    {isAdmin && pending && (
                      <div className="flex gap-1">
                        <Button onClick={() => decideCorrection(c.correction_id, "approve")} data-testid={`approve-${c.correction_id}`} className="rounded-none bg-[#00C97A] hover:bg-[#00B26B] text-black h-8 px-3 text-xs">
                          <Check className="w-3.5 h-3.5 mr-1" /> Approve
                        </Button>
                        <Button onClick={() => decideCorrection(c.correction_id, "reject")} variant="ghost" data-testid={`reject-${c.correction_id}`} className="rounded-none border border-neutral-800 hover:bg-neutral-900 h-8 px-3 text-xs">
                          <XIcon className="w-3.5 h-3.5 mr-1" /> Reject
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* History */}
        <div className="border border-neutral-900">
          <div className="px-5 py-4 border-b border-neutral-900 text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            Your Last 30 Days
          </div>
          <div className="divide-y divide-neutral-900">
            {history.length === 0 && (
              <div className="px-5 py-6 text-sm text-neutral-500">No history.</div>
            )}
            {history.map((h) => (
              <div
                key={h.attendance_id}
                className="px-5 py-3 grid grid-cols-4 gap-2 text-sm"
                data-testid={`my-history-${h.date}`}
              >
                <div className="font-mono text-neutral-400">{h.date}</div>
                <div className="text-neutral-300">{fmt(h.check_in)}</div>
                <div className="text-neutral-300">{fmt(h.check_out)}</div>
                <div className="text-right font-mono text-neutral-300">
                  {hoursBetween(h.check_in, h.check_out) || "—"} h
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Analytics */}
        {analytics && (
          <div className="border border-neutral-900">
            <div className="px-5 py-4 border-b border-neutral-900 flex flex-wrap items-center gap-3">
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                Analytics · Last {analytics.days} days
              </div>
              <div className="ml-auto flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Late after</span>
                  <Input
                    type="time"
                    value={lateAfter}
                    onChange={(e) => setLateAfter(e.target.value)}
                    data-testid="late-after-input"
                    className="w-28 h-8 rounded-none bg-neutral-900 border-neutral-800 text-xs"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Window</span>
                  <Input
                    type="number"
                    min={1}
                    max={365}
                    value={days}
                    onChange={(e) => setDays(Math.max(1, Number(e.target.value) || 30))}
                    data-testid="window-days-input"
                    className="w-20 h-8 rounded-none bg-neutral-900 border-neutral-800 text-xs"
                  />
                </div>
              </div>
            </div>
            <div className="divide-y divide-neutral-900">
              {analytics.rows.length === 0 && (
                <div className="px-5 py-6 text-sm text-neutral-500">No attendance data.</div>
              )}
              {analytics.rows.map((r) => (
                <div
                  key={r.user_id}
                  className="px-5 py-4 grid grid-cols-2 md:grid-cols-7 gap-3 items-center text-sm"
                  data-testid={`analytics-row-${r.user_id}`}
                >
                  <div className="md:col-span-2 font-medium">{r.user_name}</div>
                  <div className="text-neutral-400">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-600 block">Days</span>
                    <span className="font-mono">{r.days}</span>
                  </div>
                  <div className="text-neutral-400">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-600 block">Avg / day</span>
                    <span className="font-mono">{r.avg_hours} h</span>
                  </div>
                  <div className="text-neutral-400">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-600 block">vs 8h target</span>
                    <span className={`font-mono ${r.delta_hours >= 0 ? "text-emerald-400" : "text-amber-400"}`}>
                      {r.delta_hours >= 0 ? "+" : ""}{r.delta_hours} h
                    </span>
                  </div>
                  <div className="text-neutral-400">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-600 block">Late</span>
                    <span className={`font-mono ${r.late_count > 0 ? "text-amber-400" : ""}`}>{r.late_count}</span>
                  </div>
                  <div className="text-neutral-400">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-600 block">Incomplete</span>
                    <span className={`font-mono ${r.incomplete_count > 0 ? "text-red-400" : ""}`}>{r.incomplete_count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Admin: Team timesheet */}
        {isAdmin && (
          <div className="border border-neutral-900">
            <div className="px-5 py-4 border-b border-neutral-900 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                Team · Day View
              </div>
              <Input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                data-testid="team-date-filter"
                className="w-44 h-8 rounded-none bg-neutral-900 border-neutral-800 text-xs"
              />
            </div>
            <div className="divide-y divide-neutral-900">
              {team.length === 0 && (
                <div className="px-5 py-6 text-sm text-neutral-500">
                  Nobody marked attendance for {dateFilter}.
                </div>
              )}
              {team.map((t) => (
                <div
                  key={t.attendance_id}
                  className="px-5 py-3 grid grid-cols-5 gap-2 text-sm"
                  data-testid={`team-row-${t.user_id}`}
                >
                  <div className="font-medium">{t.user_name}</div>
                  <div className="font-mono text-neutral-400">{t.date}</div>
                  <div className="text-neutral-300">{fmt(t.check_in)}</div>
                  <div className="text-neutral-300">{fmt(t.check_out)}</div>
                  <div className="text-right font-mono text-neutral-300">
                    {hoursBetween(t.check_in, t.check_out) || "—"} h
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
